import { ChangeEvent, useState } from "react";

import { Card } from "@ezmid/components-library/";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import * as yup from "yup";

import { useCreateCourseMutation } from "../../../api/courses.generated";
import { useGetBackOfficeListLecturersQuery } from "../../../api/lecturers.generated";
import { Optional } from "../../../enums/types";
import { useUpload, useUploadMedia } from "../../../hooks";
import { makeRoute } from "../../../hooks/route";
import { useGetImage } from "../../../hooks/useGetImage";
import { handleValidationErrors } from "../../../utils/validationErrorHandler";
import { AllRoutes } from "../../route/Route";
import { CourseDetailForm } from "../molecules/CourseDetailForm";

type FormValues = {
  courseNameEN: string;
  courseNameDE: string;
  shortDescriptionEN: string;
  shortDescriptionDE: string;
  goalDescriptionEN: string;
  goalDescriptionDE: string;
  descriptionEN: string;
  descriptionDE: string;
  structureEN: string;
  structureDE: string;
  targetGroupEN: string;
  targetGroupDE: string;
  titleImageID: string;
  lecturerID: string;
  productID: string;
  status: "cs-active" | "cs-preview" | "cs-draft";
};

export function CourseAddPageContent() {
  const { t } = useTranslation();

  const [createCourse] = useCreateCourseMutation();
  const { addFileToFormData } = useUpload();
  const [isDirtyFromImage, setIsDirtyFromImage] = useState<boolean>(false);
  const { currentData } = useGetBackOfficeListLecturersQuery({ size: 1000 });

  const navigate = useNavigate();

  const lecturerOptions = currentData?.items?.map(({ id, fullName }) => ({
    value: id || "",
    label: fullName || "",
  }));

  const FormSchema = yup.object().shape({
    shortDescriptionEN: yup.string(),
    textEn: yup.string(),
    textDe: yup.string(),
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    getValues,
    setError,
  } = useForm<FormValues>({
    defaultValues: {
      courseNameEN: "",
      courseNameDE: "",
      shortDescriptionEN: "",
      shortDescriptionDE: "",
      goalDescriptionEN: "",
      goalDescriptionDE: "",
      descriptionEN: "",
      descriptionDE: "",
      structureEN: "",
      structureDE: "",
      targetGroupEN: "",
      targetGroupDE: "",
      titleImageID: "",
      lecturerID: "",
      status: "cs-active",
    },
    mode: "all",
    resolver: yupResolver(FormSchema),
  });

  const [imageFormData, setImageFormData] = useState<Optional<FormData>>(null);
  const { uploadedMediaId, isMediaUploading } = useUploadMedia(imageFormData);
  const { downloadedImage, isImageDownloading } = useGetImage(uploadedMediaId);

  const onSubmit = () => {
    const {
      descriptionDE,
      descriptionEN,
      shortDescriptionDE,
      shortDescriptionEN,
      courseNameEN,
      courseNameDE,
      goalDescriptionEN,
      goalDescriptionDE,
      structureDE,
      structureEN,
      targetGroupDE,
      targetGroupEN,
      lecturerID,
      status,
    } = getValues();

    setIsDirtyFromImage(false);
    createCourse({
      courseUpdateRequest: {
        translations: [
          {
            language: "en",
            name: courseNameEN,
            description: descriptionEN,
            shortDescription: shortDescriptionEN,
            goalDescription: goalDescriptionEN,
            structure: structureEN,
            targetGroup: targetGroupEN,
          },
          {
            language: "de",
            name: courseNameDE,
            description: descriptionDE,
            shortDescription: shortDescriptionDE,
            goalDescription: goalDescriptionDE,
            structure: structureDE,
            targetGroup: targetGroupDE,
          },
        ],
        lecturerID: lecturerID || "",
        status,
      },
    })
      .unwrap()
      .then(() => {
        reset({
          descriptionEN,
          descriptionDE,
          shortDescriptionDE,
          shortDescriptionEN,
          goalDescriptionDE,
          goalDescriptionEN,
          courseNameDE,
          courseNameEN,
        });
        toast.success(t("app.course.add.success"));
        navigate(makeRoute(AllRoutes.COURSES_LIST));
      })
      .catch((error) => {
        const { hasFieldErrors, errorMessage } = handleValidationErrors(
          error,
          setError
        );
        if (!hasFieldErrors) {
          toast.warning(errorMessage || t("app.exercise.add.failed"));
        }
      });
  };

  const onChangePicture = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) {
      setImageFormData(addFileToFormData(e.target.files[0]));
      setIsDirtyFromImage(true);
    }
  };
  const isImageLoading = isMediaUploading || isImageDownloading;
  const isFormDirty = isDirty || isDirtyFromImage;
  const isSubmitButtonDisabled = !isFormDirty || isImageLoading;

  return (
    <div className="w-full min-w-full flex justify-center pt-4">
      <div className="w-1/2 h-[calc(100%_-_382px)]">
        <Card size="fill" shadows="xl" headerText={t("app.form.create.new")}>
          <CourseDetailForm
            control={control}
            errors={errors}
            onChangePicture={onChangePicture}
            handleSubmit={handleSubmit(onSubmit)}
            courseImage={downloadedImage || ""}
            isImageLoading={isImageLoading}
            lecturerOptions={lecturerOptions || []}
            defaultLecturerValue={null}
            defaultStatusValue={{
              value: "cs-active",
              label: t("form.status.active"),
            }}
            isSubmitButtonDisabled={isSubmitButtonDisabled}
            onSubmit={onSubmit}
          />
        </Card>
      </div>
    </div>
  );
}
